import { removeAuthCookie } from '../utils/cookies';
import User from '../data/models/user';
import API from '../data/api';
import { LAST_COMPANY_KEY, clearCompany, getCompanyIdSync, runCompanyResetHandlers, setCompany } from './session';
import { FEATURE_FLAGS_STORAGE_KEY } from './featureOverrides';

/**
 * localStorage keys that survive a reset: no user or company data, only this browser's
 * preferences and the marker that it already has the company layer (session.js).
 */
export const PRESERVED_LOCAL_KEYS = Object.freeze([
    LAST_COMPANY_KEY,
    // Sidebar collapsed / expanded (components/common/sidebar/sidebar.controller.js)
    'shelf.sidebarCollapsed',
    // Feature flags per company, from central-service (./featureOverrides.js)
    FEATURE_FLAGS_STORAGE_KEY,
]);

function clearLocalStorage() {
    try {
        const store = window.localStorage;
        const keys = [];
        for (let i = 0; i < store.length; i += 1) keys.push(store.key(i));
        keys.filter((key) => key !== null && !PRESERVED_LOCAL_KEYS.includes(key)).forEach((key) => store.removeItem(key));
    } catch {
        // Storage unavailable (private mode): nothing persisted to clear.
    }
}

function clearSessionStorage() {
    try {
        window.sessionStorage.clear();
    } catch {
        // ignore
    }
}

/**
 * Forget everything the admin keeps for a signed-in user and company: the auth cookie, the
 * session company, every app key in localStorage and sessionStorage, the user and API
 * singletons and the module caches registered with `onCompanyReset` (product catalog, …).
 *
 * Runs on logout and before signing in to a company other than the stored one, so nothing of
 * one company is ever shown to (or sent with the credentials of) another. Callers then reload
 * the page (`reloadTo`) so no React state survives either.
 */
export function resetClientState() {
    removeAuthCookie();
    User.resetInstance();
    API.resetInstance();
    clearCompany();
    clearLocalStorage();
    clearSessionStorage();
    runCompanyResetHandlers();
}

/**
 * Make `company` the session company. Coming from another company, or from a browser with no
 * company yet (whatever it kept may be another company's), everything is forgotten first.
 * @param {{ companyId: string, apiUrl?: string, companyName?: string }} company
 * @returns {{ company: Object, reset: boolean }}
 */
export function switchCompany(company) {
    const reset = getCompanyIdSync() !== company?.companyId;
    if (reset) resetClientState();
    return { company: setCompany(company), reset };
}

/** Full page load of `path` (a fresh JS context). */
export function reloadTo(path = '/') {
    window.location.assign(path);
}
